import React, { useState } from 'react';
import { Button, Input, Form, Typography, Carousel, message, Grid, Alert } from 'antd'; // 👈 Añadido Alert
import { UserOutlined, LockOutlined, RocketOutlined, BulbOutlined, SafetyCertificateOutlined, CustomerServiceOutlined, PhoneOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../aws/AuthContext';
import './style.css';

const { Text } = Typography;
const { useBreakpoint } = Grid;

// --- DICCIONARIO PROFESIONAL DE ERRORES DE COGNITO ---
const obtenerMensajeError = (error) => {
    // Si el error no viene directamente de AWS, mostramos el mensaje genérico
    if (!error || !error.code) return 'Ocurrió un error inesperado al intentar iniciar sesión.';

    switch (error.code) {
        case 'NotAuthorizedException':
            // Cognito usa esta misma excepción para contraseñas malas y usuarios bloqueados.
            // Lo diferenciamos por el texto exacto que envía AWS en el mensaje.
            if (error.message === 'User is disabled.') {
                return 'Tu cuenta ha sido desactivada por el administrador. Contacta a soporte o a tu supervisor para recuperar el acceso.';
            }
            return 'El correo o la contraseña son incorrectos. Verifica tus datos e intenta de nuevo.';
            
        case 'UserNotFoundException':
            return 'No hemos encontrado ninguna cuenta registrada con este correo en nuestro sistema.';
            
        case 'UserNotConfirmedException':
            return 'Tu cuenta aún no ha sido confirmada. Por favor revisa tu bandeja de entrada o correo no deseado.';
            
        case 'TooManyRequestsException':
            return 'Demasiados intentos fallidos. Por tu seguridad, hemos bloqueado temporalmente el acceso. Espera unos minutos e intenta de nuevo.';
            
        case 'PasswordResetRequiredException':
            return 'Por motivos de seguridad, es necesario que restablezcas tu contraseña antes de continuar.';
            
        case 'LimitExceededException':
            return 'Has excedido el límite de solicitudes. Intenta de nuevo más tarde.';
            
        default:
            return 'Error de conexión con el sistema de validación. Intenta de nuevo.';
    }
};

const Login = () => {
    const { login, completeNewPassword } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('LOGIN');
    const [cognitoUserRef, setCognitoUserRef] = useState(null);
    
    // 👇 NUEVO ESTADO: Para controlar la alerta visual del error
    const [errorAcceso, setErrorAcceso] = useState(null); 

    const screens = useBreakpoint();
    const isMobile = screens.md === false;

    const onFinishLogin = async (values) => {
        setLoading(true);
        setErrorAcceso(null); // Limpiamos el error cada vez que intenta de nuevo
        
        try {
            const result = await login(values.correo, values.password);

            if (result?.status === 'NEW_PASSWORD_REQUIRED') {
                setCognitoUserRef(result.cognitoUser);
                setStep('NEW_PASSWORD');
                message.info('Es tu primer ingreso. Por seguridad, debes crear una nueva contraseña.');
            } else {
                navigate('/ventas');
            }
        } catch (error) {
            // 👇 TRADUCCIÓN E INYECCIÓN DEL ERROR
            const mensajeAmigable = obtenerMensajeError(error);
            setErrorAcceso(mensajeAmigable);
        } finally {
            setLoading(false);
        }
    };

    const onFinishNewPassword = async (values) => {
        setLoading(true);
        setErrorAcceso(null);
        
        try {
            const phone = values.telefono.startsWith('+') ? values.telefono : `+57${values.telefono}`;
            const requiredAttributes = { phone_number: phone };

            await completeNewPassword(cognitoUserRef, values.newPassword, requiredAttributes);
            message.success('¡Cuenta activada exitosamente!');
            navigate('/ventas');
        } catch (error) {
            const mensajeAmigable = obtenerMensajeError(error);
            setErrorAcceso(mensajeAmigable);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-wrapper">
            {/* COLUMNA DE LOGIN (40%) */}
            <div className="login-form-column">
                <div className="login-box-container">
                    <div className="login-brand-header">
                        <a href='/'>
                            <img src="/img/g1.png" alt="Yugonub Logo" title='Página de inicio' style={{ cursor: 'pointer', width: !isMobile ? "340px" : "320px", height: !isMobile ? "58px" : "60px" }} />
                        </a>
                        <Text type="secondary" className="brand-subtitle">Software Administrativo Y POS</Text>
                    </div>

                    {/* 👇 COMPONENTE ALERT: Se muestra solo si hay un error */}
                    {errorAcceso && (
                        <Alert 
                            message="Acceso Denegado" 
                            description={errorAcceso} 
                            type="error" 
                            showIcon 
                            style={{ marginBottom: '24px', borderRadius: '8px', border: '1px solid #ffccc7' }} 
                        />
                    )}

                    {step === 'LOGIN' ? (
                        <Form layout="vertical" onFinish={onFinishLogin} size="large" validateTrigger="onBlur">
                            <Form.Item name="correo" rules={[{ required: true, message: 'Por favor ingrese su correo' }, { type: 'email', message: 'Ingrese un formato de correo válido' }]}>
                                <Input prefix={<UserOutlined style={{ color: '#22BABB' }} />} placeholder="Correo electrónico" />
                            </Form.Item>
                            <Form.Item name="password" rules={[{ required: true, message: 'La contraseña es obligatoria' }]}>
                                <Input.Password prefix={<LockOutlined style={{ color: '#22BABB' }} />} placeholder="Contraseña" />
                            </Form.Item>
                            
                            <div style={{ textAlign: 'right', marginBottom: '20px', marginTop: '-10px' }}>
                                <Link style={{ color: '#747F8E', fontSize: '13px' }} to="/recuperar-clave">¿Olvidaste tu contraseña?</Link>
                            </div>

                            <Button type="primary" htmlType="submit" block loading={loading} className="yugonub-btn-primary">
                                INGRESAR AL SISTEMA
                            </Button>
                        </Form>
                    ) : (
                        <Form layout="vertical" onFinish={onFinishNewPassword} size="large" validateTrigger="onBlur">
                            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                                <Text strong>¡Hola! Has sido invitado a Yugonub.</Text>
                                <br />
                                <Text type="secondary">Crea tu contraseña definitiva y completa tu perfil para ingresar.</Text>
                            </div>

                            <Form.Item
                                name="newPassword"
                                rules={[{ required: true, message: 'La contraseña es obligatoria' }, { min: 8, message: 'Debe tener al menos 8 caracteres' }]}
                            >
                                <Input.Password prefix={<LockOutlined style={{ color: '#22BABB' }} />} placeholder="Nueva contraseña" />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                dependencies={['newPassword']}
                                rules={[
                                    { required: true, message: 'Por favor confirma tu contraseña' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('newPassword') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Las contraseñas no coinciden'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined style={{ color: '#22BABB' }} />} placeholder="Confirmar contraseña" />
                            </Form.Item>

                            <Form.Item
                                name="telefono"
                                required
                                rules={[{ required: true, message: 'El número de teléfono es obligatorio' }]}
                            >
                                <Input prefix={<PhoneOutlined style={{ color: '#22BABB' }} />} placeholder="Número de celular (ej. 314...)" />
                            </Form.Item>

                            <Button type="primary" htmlType="submit" block loading={loading} className="yugonub-btn-primary">
                                GUARDAR Y ENTRAR
                            </Button>
                            <Button type="link" block onClick={() => { setStep('LOGIN'); setErrorAcceso(null); }} style={{ marginTop: '10px' }}>
                                Cancelar
                            </Button>
                        </Form>
                    )}

                    {step === 'LOGIN' && (
                        <div className="login-form-footer" style={{ marginTop: '24px' }}>
                            <Text>¿No tienes una cuenta? <Link style={{ color: '#22BABB', fontWeight: 'bold' }} to="/registro">Regístrate ahora</Link></Text>
                        </div>
                    )}
                </div>

                <div className="login-legal-fixed">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm">
                        <Link style={{ color: '#747F8E', fontWeight: '500' }} to="/terminos-y-condiciones">Términos y Condiciones</Link>
                        <span className="hidden sm:inline text-gray-300">|</span>
                        <Link style={{ color: '#747F8E', fontWeight: '500' }} to="/politica-privacidad">Política de Privacidad</Link>
                        <span className="hidden sm:inline text-gray-300">|</span>
                        <Text style={{ color: '#747F8E' }} type="secondary">v1.0.0</Text>
                    </div>
                </div>
            </div>

            {/* COLUMNA DE PUBLICIDAD (60%) */}
            <div className="login-ad-column">
                <Carousel autoplay effect="fade" autoplaySpeed={5000}>
                    {/* Slide 1: Novedades */}
                    <div className="ad-slide-item slide-novedades">
                        <div className="ad-overlay">
                            <RocketOutlined className="ad-main-icon" />
                            <h2>Novedades</h2>
                            <p>¡Nueva función de reportes disponible!</p>
                        </div>
                    </div>

                    {/* Slide 2: Tips */}
                    <div className="ad-slide-item slide-tips">
                        <div className="ad-overlay">
                            <BulbOutlined className="ad-main-icon" />
                            <h2>Tips de uso</h2>
                            <p>¿Sabías que puedes cargar tu inventario masivamente?</p>
                        </div>
                    </div>

                    {/* Slide 3: Seguridad */}
                    <div className="ad-slide-item slide-seguridad">
                        <div className="ad-overlay">
                            <SafetyCertificateOutlined className="ad-main-icon" />
                            <h2>Seguridad</h2>
                            <p>Tu información está protegida con cifrado de grado bancario.</p>
                        </div>
                    </div>

                    {/* Slide 4: Soporte */}
                    <div className="ad-slide-item slide-soporte">
                        <div className="ad-overlay">
                            <CustomerServiceOutlined className="ad-main-icon" />
                            <h2>Soporte</h2>
                            <p>Línea de atención exclusiva para clientes.</p>
                        </div>
                    </div>
                </Carousel>
            </div>
        </div>
    );
};

export default Login;